import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import {
  CalendarClock,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CalendarIcon,
  Clock,
  MapPin,
  User,
  FileText,
  CheckCircle2,
  XCircle,
  Repeat,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO, isToday, isTomorrow, addDays, addMonths } from "date-fns";
import { ro } from "date-fns/locale";
import { API } from "../config/api";
import { cn } from "../lib/utils";
import { ClientSelect } from "../components/ClientSelect";
import { CaseSelect } from "../components/CaseSelect";
import { useDataCache } from "../context/DataCacheContext";


const STATUS_CONFIG = {
  scheduled: { label: "Programat", color: "bg-blue-500/10 text-blue-600 border-blue-200", icon: Clock },
  completed: { label: "Finalizat", color: "bg-green-500/10 text-green-600 border-green-200", icon: CheckCircle2 },
  cancelled: { label: "Anulat", color: "bg-red-500/10 text-red-600 border-red-200", icon: XCircle },
};


export default function AppointmentsPage() {
  const { user, getAuthHeader } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [filter, setFilter] = useState("upcoming");
  const [saving, setSaving] = useState(false);
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState(null);
  const appointmentRefs = useRef({});
  
  const isAdmin = user?.role === "admin";
  const isAvocatColaborator = user?.position === "avocat_colaborator";

  const [formData, setFormData] = useState({
    client_id: "",
    client_name: "",
    title: "Consultație juridică",
    case_id: "",
    date: "",
    time: "10:00",
    duration_minutes: 60,
    location: "Birou",
    assigned_to_id: "",
    notes: "",
    recurrence_type: "none",
    recurrence_end_date: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [appointmentsRes, clientsRes, casesRes, usersRes] = await Promise.all([
        axios.get(`${API}/appointments`, getAuthHeader()),
        axios.get(`${API}/clients`, getAuthHeader()),
        axios.get(`${API}/cases`, getAuthHeader()),
        user?.role === "admin" ? axios.get(`${API}/users`, getAuthHeader()) : Promise.resolve({ data: [] }),
      ]);
      setAppointments(appointmentsRes.data);
      setClients(clientsRes.data);
      setCases(casesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle highlighting appointment from URL parameter
  useEffect(() => {
    const appointmentId = searchParams.get("appointment");
    if (appointmentId && appointments.length > 0) {
      setHighlightedAppointmentId(appointmentId);
      // Scroll to the appointment
      setTimeout(() => {
        if (appointmentRefs.current[appointmentId]) {
          appointmentRefs.current[appointmentId].scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      // Clear the URL parameter
      setSearchParams({});
      // Remove highlight after 3 seconds
      setTimeout(() => setHighlightedAppointmentId(null), 3000);
    }
  }, [searchParams, appointments, setSearchParams]);

  const handleOpenDialog = (appointment = null) => {
    if (appointment) {
      setSelectedAppointment(appointment);
      setFormData({
        client_id: appointment.client_id || "",
        client_name: appointment.client_name || "",
        title: appointment.title || "Consultație juridică",
        case_id: appointment.case_id || "",
        date: appointment.date,
        time: appointment.time,
        duration_minutes: appointment.duration_minutes || 60,
        location: appointment.location || "Birou",
        assigned_to_id: appointment.assigned_to_id || "",
        notes: appointment.notes || "",
        recurrence_type: appointment.recurrence_type || "none",
        recurrence_end_date: appointment.recurrence_end_date || "",
      });
    } else {
      setSelectedAppointment(null);
      setFormData({
        client_id: "",
        client_name: "",
        title: "Consultație juridică",
        case_id: "",
        date: format(new Date(), "yyyy-MM-dd"),
        time: "10:00",
        duration_minutes: 60,
        location: "Birou",
        assigned_to_id: user?.id || "",
        notes: "",
        recurrence_type: "none",
        recurrence_end_date: "",
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.client_name || !formData.date || !formData.time) {
      toast.error("Completați clientul, data și ora");
      return;
    }

    setSaving(true);
    try {
      const effectiveTitle = formData.title?.trim() || "Consultație juridică";
      const payload = {
        title: effectiveTitle,
        date: formData.date,
        time: formData.time,
        duration_minutes: formData.duration_minutes,
        location: formData.location || null,
        notes: formData.notes || null,
        assigned_to_id: formData.assigned_to_id || null,
        case_id: formData.case_id || null,
      };

      // Recurrence (only on create, not edit)
      if (!selectedAppointment && formData.recurrence_type && formData.recurrence_type !== "none") {
        payload.recurrence_type = formData.recurrence_type;
        payload.recurrence_end_date = formData.recurrence_end_date || format(addMonths(new Date(), 3), "yyyy-MM-dd");
      }

      // Handle client
      if (formData.client_id) {
        payload.client_id = formData.client_id;
      } else if (formData.client_name?.trim()) {
        payload.client_id = null;
        payload.new_client_name = formData.client_name.trim();
      } else {
        payload.client_id = null;
      }

      if (selectedAppointment) {
        await axios.put(`${API}/appointments/${selectedAppointment.id}`, payload, getAuthHeader());
        toast.success("Programare actualizată");
      } else {
        const res = await axios.post(`${API}/appointments`, payload, getAuthHeader());
        const count = Array.isArray(res.data) ? res.data.length : 1;
        toast.success(count > 1 ? `${count} programări create` : "Programare creată");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAppointment) return;
    try {
      await axios.delete(`${API}/appointments/${selectedAppointment.id}`, getAuthHeader());
      toast.success("Programare ștearsă");
      setDeleteDialogOpen(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedAppointment) return;
    try {
      const res = await axios.delete(`${API}/appointments/${selectedAppointment.id}/group`, getAuthHeader());
      toast.success(res.data.message || "Programări șterse");
      setDeleteGroupDialogOpen(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      if (newStatus === "completed") {
        await axios.post(`${API}/appointments/${appointmentId}/complete`, {}, getAuthHeader());
      } else if (newStatus === "cancelled") {
        await axios.post(`${API}/appointments/${appointmentId}/cancel`, {}, getAuthHeader());
      }
      toast.success("Status actualizat");
      fetchData();
    } catch (error) {
      toast.error("Eroare la actualizarea statusului");
    }
  };

  const filteredAppointments = appointments.filter((apt) => {
    const aptDate = parseISO(apt.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (filter === "upcoming") {
      return aptDate >= today && apt.status !== "cancelled";
    } else if (filter === "past") {
      return aptDate < today || apt.status === "completed";
    }
    return true;
  });

  const getDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Astăzi";
    if (isTomorrow(date)) return "Mâine";
    return format(date, "EEEE, dd/MM/yyyy", { locale: ro });
  };

  const groupedAppointments = filteredAppointments.reduce((groups, apt) => {
    const date = apt.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(apt);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedAppointments).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <CalendarClock className="w-8 h-8 text-primary" />
            Programări Clienți
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestionează întâlnirile cu clienții
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="upcoming">Viitoare</SelectItem>
              <SelectItem value="past">Trecute</SelectItem>
              <SelectItem value="all">Toate</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => handleOpenDialog()} data-testid="add-appointment-btn">
            <Plus className="w-4 h-4 mr-2" />
            Programare Nouă
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Astăzi</p>
                <p className="text-2xl font-bold">
                  {appointments.filter(a => a.date === format(new Date(), "yyyy-MM-dd") && a.status === "scheduled").length}
                </p>
              </div>
              <CalendarIcon className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Săptămâna aceasta</p>
                <p className="text-2xl font-bold">
                  {appointments.filter(a => {
                    const d = parseISO(a.date);
                    const weekEnd = addDays(new Date(), 7);
                    return d >= new Date() && d <= weekEnd && a.status === "scheduled";
                  }).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Finalizate</p>
                <p className="text-2xl font-bold">
                  {appointments.filter(a => a.status === "completed").length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{appointments.length}</p>
              </div>
              <CalendarClock className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>
      </div>{/* End Sticky */}

      {/* Appointments List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista Programărilor</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedDates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarClock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nu există programări {filter === "upcoming" ? "viitoare" : filter === "past" ? "trecute" : ""}</p>
            </div>
          ) : (
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-6">
                {sortedDates.map((date) => (
                  <div key={date}>
                    <h3 className="font-semibold text-sm text-muted-foreground mb-3 sticky top-0 bg-background py-2">
                      {getDateLabel(date)}
                    </h3>
                    <div className="space-y-1.5">
                      {groupedAppointments[date].map((apt) => {
                        // Border color per status
                        let borderClass = "border-l-[3px] ";
                        let statusBadgeClass = "";
                        let statusLabel = "";
                        if (apt.status === "completed") {
                          borderClass += "border-l-green-500 bg-green-50/30 opacity-60";
                          statusLabel = "Finalizat";
                          statusBadgeClass = "bg-green-100 text-green-700";
                        } else if (apt.status === "cancelled") {
                          borderClass += "border-l-gray-400 bg-gray-50/30 opacity-50";
                          statusLabel = "Anulat";
                          statusBadgeClass = "bg-gray-100 text-gray-600";
                        } else {
                          // scheduled
                          const aptDate = new Date(apt.date + "T" + apt.time);
                          const now = new Date();
                          const diffMs = aptDate - now;
                          const diffHours = diffMs / (1000 * 60 * 60);
                          if (diffMs < 0) {
                            borderClass += "border-l-red-500 bg-red-50/30";
                            statusLabel = "Programat";
                            statusBadgeClass = "bg-red-100 text-red-700";
                          } else if (diffHours < 24) {
                            borderClass += "border-l-orange-500 bg-orange-50/30";
                            statusLabel = "Programat";
                            statusBadgeClass = "bg-orange-100 text-orange-700";
                          } else {
                            borderClass += "border-l-blue-500";
                            statusLabel = "Programat";
                            statusBadgeClass = "bg-blue-100 text-blue-700";
                          }
                        }

                        return (
                          <div
                            key={apt.id}
                            ref={(el) => (appointmentRefs.current[apt.id] = el)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all cursor-pointer hover:bg-secondary/30 ${borderClass} ${
                              highlightedAppointmentId === apt.id ? "ring-2 ring-primary ring-offset-2 bg-primary/5" : ""
                            }`}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                              {/* Ora */}
                              <span className="font-mono text-sm font-semibold text-primary shrink-0">
                                {apt.time}
                              </span>
                              {/* Client */}
                              <span className={`font-body text-sm font-medium truncate ${
                                apt.status === "cancelled" ? "line-through text-muted-foreground" : ""
                              }`}>
                                {apt.client_name || apt.title || "—"}
                              </span>
                              {/* Dosar */}
                              {apt.case_number && (
                                <>
                                  <span className="text-muted-foreground shrink-0">|</span>
                                  <Badge variant="outline" className="font-mono text-[10px] px-1 py-0 shrink-0">{apt.case_number}</Badge>
                                </>
                              )}
                              {/* Descriere */}
                              {apt.title && (
                                <>
                                  <span className="text-muted-foreground shrink-0">|</span>
                                  <span className="text-xs text-muted-foreground truncate">{apt.title}</span>
                                </>
                              )}
                              {/* Durata */}
                              <span className="text-muted-foreground shrink-0">|</span>
                              <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                                {apt.duration_minutes}min
                              </span>
                              {/* Avocat */}
                              {apt.assigned_to_name && (
                                <>
                                  <span className="text-muted-foreground shrink-0">|</span>
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">{apt.assigned_to_name}</Badge>
                                </>
                              )}
                              {/* Locație */}
                              {apt.location && (
                                <>
                                  <span className="text-muted-foreground shrink-0">|</span>
                                  <span className="text-[10px] text-muted-foreground shrink-0">{apt.location}</span>
                                </>
                              )}
                              {/* Recurență */}
                              {apt.recurrence_type && apt.recurrence_type !== "none" && (
                                <>
                                  <span className="text-muted-foreground shrink-0">|</span>
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 text-violet-600 border-violet-300 shrink-0">
                                    <Repeat className="w-3 h-3 mr-0.5" />
                                    {apt.recurrence_type === "daily" ? "Zilnic" : apt.recurrence_type === "weekly" ? "Săpt." : apt.recurrence_type === "biweekly" ? "Bisăpt." : apt.recurrence_type === "monthly" ? "Lunar" : ""}
                                  </Badge>
                                </>
                              )}
                              {/* Stadiu */}
                              <span className="text-muted-foreground shrink-0">|</span>
                              <Badge className={`text-[10px] px-1.5 py-0 shrink-0 ${statusBadgeClass}`}>{statusLabel}</Badge>
                            </div>
                            {/* Actions */}
                            <div className="flex items-center gap-0.5 shrink-0">
                              {apt.status === "scheduled" && (
                                <>
                                  <Button size="sm" variant="outline" className="h-6 px-1.5 text-green-600 hover:bg-green-50" onClick={() => handleStatusChange(apt.id, "completed")}>
                                    <CheckCircle2 className="w-3 h-3" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleOpenDialog(apt)}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-6 px-1.5 text-red-600 hover:bg-red-50" onClick={() => handleStatusChange(apt.id, "cancelled")}>
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                  {apt.recurrence_group_id && (
                                    <Button size="sm" variant="outline" className="h-6 px-1.5 text-destructive text-[10px]" onClick={() => { setSelectedAppointment(apt); setDeleteGroupDialogOpen(true); }}>
                                      <Trash2 className="w-3 h-3 mr-0.5" />Seria
                                    </Button>
                                  )}
                                </>
                              )}
                              {(apt.status === "completed" || apt.status === "cancelled") && (
                                <>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => { setSelectedAppointment(apt); setDeleteDialogOpen(true); }}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                  {apt.recurrence_group_id && (
                                    <Button size="sm" variant="outline" className="h-6 px-1.5 text-destructive text-[10px]" onClick={() => { setSelectedAppointment(apt); setDeleteGroupDialogOpen(true); }}>
                                      <Trash2 className="w-3 h-3 mr-0.5" />Toate
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedAppointment ? "Editare Programare" : "Programare Nouă"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* 1. Client * */}
            <div>
              <label className="text-sm font-medium mb-1 block">Client *</label>
              <ClientSelect
                clients={clients}
                value={formData.client_id || "none"}
                onValueChange={(value, customName) => {
                  if (value === "__custom__") {
                    setFormData({ ...formData, client_id: "", client_name: customName || "" });
                  } else {
                    const selected = clients.find(c => c.id === value);
                    setFormData({
                      ...formData,
                      client_id: value === "none" ? "" : value,
                      client_name: selected ? (selected.nume || selected.denumire || "") : "",
                    });
                  }
                }}
                placeholder="Selectează sau scrie nume client"
                emptyText="Niciunul"
                allowNone={false}
                allowCustom={true}
                customDisplayName={!formData.client_id && formData.client_name ? formData.client_name : ""}
                data-testid="select-client"
              />
            </div>

            {/* 2. Descriere (optional, default "Consultație juridică") */}
            <div>
              <label className="text-sm font-medium mb-1 block">Descriere (Opțional)</label>
              <Input
                placeholder="Consultație juridică"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            {/* 3. Dosar Asociat (optional) */}
            <div>
              <label className="text-sm font-medium mb-1 block">Dosar Asociat (Opțional)</label>
              <CaseSelect
                cases={cases}
                value={formData.case_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, case_id: value === "none" ? "" : value })}
                placeholder="Selectează dosar"
                emptyText="Fără dosar"
                allowNone={true}
                data-testid="select-case"
              />
            </div>

            {/* 4-5. Data / Ora */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Data *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formData.date
                        ? format(parseISO(formData.date), "dd/MM/yyyy", { locale: ro })
                        : "Selectează data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.date ? parseISO(formData.date) : undefined}
                      onSelect={(date) =>
                        setFormData({ ...formData, date: date ? format(date, "yyyy-MM-dd") : "" })
                      }
                      locale={ro}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Ora *</label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                />
              </div>
            </div>

            {/* 6-7. Durată / Locație */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Durată (minute)</label>
                <Select
                  value={String(formData.duration_minutes)}
                  onValueChange={(v) => setFormData({ ...formData, duration_minutes: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">1 oră</SelectItem>
                    <SelectItem value="90">1.5 ore</SelectItem>
                    <SelectItem value="120">2 ore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Locație</label>
                <Input
                  placeholder="Birou"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>
            </div>

            {/* 8. Repartizat către */}
            {user?.role === "admin" && users.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-1 block">Repartizat către</label>
                <Select
                  value={formData.assigned_to_id || "none"}
                  onValueChange={(v) => setFormData({ ...formData, assigned_to_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează persoana" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} {u.id === user?.id ? "(eu)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* 9. Note */}
            <div>
              <label className="text-sm font-medium mb-1 block">Note</label>
              <Textarea
                placeholder="Detalii suplimentare..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            {/* 10. Recurență - only show when creating */}
            {!selectedAppointment && (
              <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-violet-600" />
                  <label className="text-sm font-medium">Recurență</label>
                </div>
                <Select
                  value={formData.recurrence_type}
                  onValueChange={(v) => {
                    const updates = { recurrence_type: v };
                    if (v !== "none" && !formData.recurrence_end_date) {
                      updates.recurrence_end_date = format(addMonths(new Date(), 3), "yyyy-MM-dd");
                    }
                    setFormData({ ...formData, ...updates });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">O singură dată</SelectItem>
                    <SelectItem value="daily">Zilnic</SelectItem>
                    <SelectItem value="weekly">Săptămânal</SelectItem>
                    <SelectItem value="biweekly">La 2 săptămâni</SelectItem>
                    <SelectItem value="monthly">Lunar</SelectItem>
                  </SelectContent>
                </Select>
                {formData.recurrence_type && formData.recurrence_type !== "none" && (
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Până la data:</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formData.recurrence_end_date
                            ? format(parseISO(formData.recurrence_end_date), "dd/MM/yyyy", { locale: ro })
                            : "Selectează data de final"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.recurrence_end_date ? parseISO(formData.recurrence_end_date) : undefined}
                          onSelect={(date) =>
                            setFormData({ ...formData, recurrence_end_date: date ? format(date, "yyyy-MM-dd") : "" })
                          }
                          locale={ro}
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {selectedAppointment ? "Salvează" : "Creează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Șterge programarea?</AlertDialogTitle>
            <AlertDialogDescription>
              Această acțiune nu poate fi anulată. Programarea va fi ștearsă permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Group Confirmation */}
      <AlertDialog open={deleteGroupDialogOpen} onOpenChange={setDeleteGroupDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Șterge toate programările recurente?</AlertDialogTitle>
            <AlertDialogDescription>
              Această acțiune va șterge TOATE programările din seria recurentă. Acțiunea nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteGroup} className="bg-destructive text-destructive-foreground">
              Șterge Toate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
